/**
 * `clawnify sessions` — the FDE's observe verb.
 *
 *   clawnify sessions [list]        list stored sessions across ALL agents on
 *                                   the org's server (main + specialists)
 *   clawnify sessions history <key> read one session's transcript by key
 *
 * Backed by the org-scoped GET /servers/:id/sessions[/history] API (read-only,
 * ownership-checked). See docs/internal/fde-strategy.md.
 */

import { api } from "../lib/api.js";
import { resolveServerId } from "../lib/servers.js";

interface SessionRow {
  agentId: string;
  key: string;
  sessionId: string | null;
  updatedAt: string | null;
  model: string | null;
}

interface ListOptions {
  server?: string;
  agent?: string;
  active?: string;
  limit?: string;
  json?: boolean;
}

interface HistoryOptions {
  server?: string;
  agent?: string;
  limit?: string;
  json?: boolean;
}

export async function sessionsListCommand(opts: ListOptions): Promise<void> {
  const serverId = await resolveServerId(opts.server);

  const params = new URLSearchParams();
  if (opts.agent) params.set("agentId", opts.agent);
  if (opts.active) params.set("activeMinutes", opts.active);
  if (opts.limit) params.set("limit", opts.limit);
  const qs = params.toString();

  const res = await api.get(`/servers/${serverId}/sessions${qs ? `?${qs}` : ""}`);
  if (!res.ok) {
    console.error(`Failed to list sessions (${res.status}).`);
    process.exit(1);
  }
  const data = (await res.json()) as {
    sessions: SessionRow[];
    totalCount?: number;
    hasMore?: boolean;
  };
  const sessions = data.sessions ?? [];

  if (opts.json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  if (sessions.length === 0) {
    console.log("No sessions found.");
    return;
  }

  const agentW = Math.max(5, ...sessions.map((s) => (s.agentId ?? "").length));
  const keyW = Math.max(3, ...sessions.map((s) => (s.key ?? "").length));
  console.log(
    `${"Agent".padEnd(agentW)}  ${"Key".padEnd(keyW)}  ${"Updated".padEnd(19)}  Model`,
  );
  console.log(
    `${"─".repeat(agentW)}  ${"─".repeat(keyW)}  ${"─".repeat(19)}  ${"─".repeat(20)}`,
  );
  for (const s of sessions) {
    const updated = (s.updatedAt ?? "").replace("T", " ").slice(0, 19);
    console.log(
      `${(s.agentId ?? "").padEnd(agentW)}  ${(s.key ?? "").padEnd(keyW)}  ${updated.padEnd(19)}  ${s.model ?? ""}`,
    );
  }
  if (data.hasMore) {
    console.log(
      `\n… more available — raise --limit (showing ${sessions.length}${data.totalCount ? ` of ${data.totalCount}` : ""}).`,
    );
  }
}

export async function sessionsHistoryCommand(
  key: string,
  opts: HistoryOptions,
): Promise<void> {
  const serverId = await resolveServerId(opts.server);

  const params = new URLSearchParams();
  params.set("key", key);
  if (opts.agent) params.set("agentId", opts.agent);
  if (opts.limit) params.set("limit", opts.limit);

  const res = await api.get(
    `/servers/${serverId}/sessions/history?${params.toString()}`,
  );
  if (!res.ok) {
    if (res.status === 404) {
      console.error(
        `No session found for key "${key}". List sessions with \`clawnify sessions\`.`,
      );
    } else {
      console.error(`Failed to read session (${res.status}).`);
    }
    process.exit(1);
  }
  const data = (await res.json()) as {
    agentId: string;
    key: string;
    sessionId: string | null;
    entries: unknown[];
    entryCount: number;
    totalLines: number;
    hasMore?: boolean;
  };

  if (opts.json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  console.log(
    `agent: ${data.agentId}   key: ${data.key}   session: ${data.sessionId ?? "?"}`,
  );
  console.log(
    `${data.entryCount} entr${data.entryCount === 1 ? "y" : "ies"}${data.hasMore ? ` (of ${data.totalLines}; raise --limit for more)` : ""}\n`,
  );
  // Raw JSONL transcript entries — one per line, so a coding agent can parse
  // them and a human can `| jq`.
  for (const entry of data.entries) {
    console.log(JSON.stringify(entry));
  }
}
